import { useState, useEffect, createContext, useContext, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import "./App.css";
import LandingPage from "./components/LandingPage";
import AuthPage from "./components/AuthPage";
import ParentDashboard from "./components/ParentDashboard";
import ChildDashboard from "./components/ChildDashboard";
import LessonPage from "./components/LessonPage";
import TasksPage from "./components/TasksPage";
import RewardsPage from "./components/RewardsPage";
import FamilyStudyPage from "./components/FamilyStudyPage";
import LessonHistoryPage from "./components/LessonHistoryPage";
import SubscriptionPage from "./components/SubscriptionPage";
import SubscriptionSuccess from "./components/SubscriptionSuccess";
import ChurchDashboard from "./components/ChurchDashboard";
import ChurchAuthPage from "./components/ChurchAuthPage";
import ChurchFamiliesPage from "./components/ChurchFamiliesPage";
import ChurchStudyPage from "./components/ChurchStudyPage";
import ChurchAnnouncementsPage from "./components/ChurchAnnouncementsPage";
import ChurchLeaderboardPage from "./components/ChurchLeaderboardPage";
import ChurchFamilyAuthPage from "./components/ChurchFamilyAuthPage";
import ChurchFamilyDashboard from "./components/ChurchFamilyDashboard";
import KidsLoginPage from "./components/KidsLoginPage";
import TermsPage from "./components/TermsPage";
import PrivacyPage from "./components/PrivacyPage";
import AnalyticsDashboard from "./components/AnalyticsDashboard";
import AdminAuthPage from "./components/AdminAuthPage";
import AdminDashboard from "./components/AdminDashboard";
import { LanguageProvider } from "./hooks/useLanguage";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Set axios auth header on initial load if token exists
const initialToken = localStorage.getItem("token");
if (initialToken) {
  axios.defaults.headers.common["Authorization"] = `Bearer ${initialToken}`;
}

export const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

const ProtectedRoute = ({ children }) => {
  const { token } = useAuth();
  return token ? children : <Navigate to="/auth" replace />;
};

// Special route that allows pending auth from localStorage (for subscription success)
const SubscriptionSuccessRoute = ({ children }) => {
  const { token, login } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState(null);
  const [processedPendingAuth, setProcessedPendingAuth] = useState(false);

  useEffect(() => {
    const processAuth = () => {
      console.log("SubscriptionSuccessRoute: Processing auth...");
      console.log("Current token from context:", token ? "exists" : "none");
      
      // Check localStorage directly first - this is more reliable
      const storedToken = localStorage.getItem("token");
      console.log("Token in localStorage:", storedToken ? "exists" : "none");
      
      // If we already have a token (either from context or localStorage), we're good
      if (token || storedToken) {
        console.log("Already authenticated via existing token");
        setIsAuthenticated(true);
        setIsLoading(false);
        return;
      }

      // Check for pending auth from registration (stored before Stripe redirect)
      const pendingAuth = localStorage.getItem("pending_auth");
      console.log("Pending auth:", pendingAuth ? "found" : "not found");
      
      if (pendingAuth && !processedPendingAuth) {
        try {
          const parsed = JSON.parse(pendingAuth);
          console.log("Parsed pending auth for user:", parsed.user?.email);
          
          if (parsed.token && parsed.user) {
            // Login with pending credentials - this will update localStorage and state
            login(parsed.token, parsed.user);
            localStorage.removeItem("pending_auth");
            setProcessedPendingAuth(true);
            // Don't set isLoading false yet - wait for token to propagate
            console.log("Login called, waiting for token to propagate...");
            return; // Return and let the token useEffect handle completion
          }
        } catch (e) {
          console.error("Error with pending auth:", e);
          setError(e.message);
          localStorage.removeItem("pending_auth");
        }
      }
      
      // If we get here with no token and no pending auth, show session expired
      if (!pendingAuth && !storedToken && !token) {
        console.log("No auth found - session expired");
      }
      setIsLoading(false);
    };

    processAuth();
  }, [processedPendingAuth]); // Re-run if we processed pending auth

  // Update isAuthenticated when token changes (from login call)
  useEffect(() => {
    if (token) {
      console.log("Token now available in context, completing auth");
      setIsAuthenticated(true);
      setIsLoading(false);
    }
  }, [token]);

  // Show loading while processing auth
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#F0F4FF" }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#06D6A0] border-t-transparent mx-auto mb-4"></div>
          <p className="text-[#073B4C] font-bold">Processing your payment...</p>
        </div>
      </div>
    );
  }

  // Show error if something went wrong
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#F0F4FF" }}>
        <div className="text-center bg-white p-8 rounded-2xl shadow-lg max-w-md">
          <p className="text-red-500 font-bold mb-4">Something went wrong</p>
          <p className="text-gray-600 mb-4">{error}</p>
          <a href="/auth" className="text-[#118AB2] font-bold hover:underline">Return to Login</a>
        </div>
      </div>
    );
  }

  // If authenticated, show page
  if (isAuthenticated || token) return children;
  
  // No auth at all - show helpful message instead of redirecting
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "#F0F4FF" }}>
      <div className="text-center bg-white p-8 rounded-2xl shadow-lg max-w-md">
        <div className="w-16 h-16 rounded-full bg-[#FFD166] flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-[#073B4C] mb-2">Session Expired</h2>
        <p className="text-gray-600 mb-4">
          Your login session was not found. This can happen if you took too long on the payment page.
        </p>
        <a href="/auth" className="inline-block bg-[#118AB2] text-white font-bold px-6 py-3 rounded-xl hover:bg-[#0d7a9e] transition-colors">
          Return to Login
        </a>
        <p className="text-gray-400 text-sm mt-4">
          If you completed payment, check your email for confirmation and try logging in.
        </p>
      </div>
    </div>
  );
};

const ChurchRoute = ({ children }) => {
  const { token, user } = useAuth();
  if (!token) return <Navigate to="/church/auth" replace />;
  if (user?.role !== "church_admin") return <Navigate to="/parent" replace />;
  return children;
};

const ChurchFamilyRoute = ({ children }) => {
  const { token, user } = useAuth();
  if (!token) return <Navigate to="/church-family/auth" replace />;
  if (user?.role !== "church_family") return <Navigate to="/parent" replace />;
  return children;
};

function AppRoutes() {
  const { token, user } = useAuth();
  
  // Redirect based on user role
  const getDefaultRoute = () => {
    if (!token) return <LandingPage />;
    if (user?.role === "church_admin") return <Navigate to="/church" replace />;
    if (user?.role === "church_family") return <Navigate to="/church-family" replace />;
    return <Navigate to="/parent" replace />;
  };

  return (
    <Routes>
      <Route path="/" element={getDefaultRoute()} />
      <Route path="/auth" element={token ? <Navigate to="/parent" replace /> : <AuthPage />} />
      <Route path="/parent" element={<ProtectedRoute><ParentDashboard /></ProtectedRoute>} />
      <Route path="/parent/history" element={<ProtectedRoute><LessonHistoryPage /></ProtectedRoute>} />
      <Route path="/parent/analytics" element={<ProtectedRoute><AnalyticsDashboard /></ProtectedRoute>} />
      <Route path="/subscription" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
      <Route path="/subscription/success" element={<SubscriptionSuccessRoute><SubscriptionSuccess /></SubscriptionSuccessRoute>} />
      <Route path="/child/:childId" element={<ProtectedRoute><ChildDashboard /></ProtectedRoute>} />
      <Route path="/child/:childId/lesson" element={<ProtectedRoute><LessonPage /></ProtectedRoute>} />
      <Route path="/child/:childId/tasks" element={<ProtectedRoute><TasksPage /></ProtectedRoute>} />
      <Route path="/child/:childId/rewards" element={<ProtectedRoute><RewardsPage /></ProtectedRoute>} />
      <Route path="/family" element={<ProtectedRoute><FamilyStudyPage /></ProtectedRoute>} />
      <Route path="/kids" element={<KidsLoginPage />} />
      
      {/* Church Routes */}
      <Route path="/church/auth" element={token && user?.role === "church_admin" ? <Navigate to="/church" replace /> : <ChurchAuthPage />} />
      <Route path="/church" element={<ChurchRoute><ChurchDashboard /></ChurchRoute>} />
      <Route path="/church/families" element={<ChurchRoute><ChurchFamiliesPage /></ChurchRoute>} />
      <Route path="/church/study" element={<ChurchRoute><ChurchStudyPage /></ChurchRoute>} />
      <Route path="/church/announcements" element={<ChurchRoute><ChurchAnnouncementsPage /></ChurchRoute>} />
      <Route path="/church/leaderboard" element={<ChurchRoute><ChurchLeaderboardPage /></ChurchRoute>} />
      
      {/* Church Family Routes (families that joined via church invitation code) */}
      <Route path="/church-family/auth" element={token && user?.role === "church_family" ? <Navigate to="/church-family" replace /> : <ChurchFamilyAuthPage />} />
      <Route path="/church-family" element={<ChurchFamilyRoute><ChurchFamilyDashboard /></ChurchFamilyRoute>} />
      <Route path="/church-family/study" element={<ChurchFamilyRoute><FamilyStudyPage /></ChurchFamilyRoute>} />
      <Route path="/church-family/leaderboard" element={<ChurchFamilyRoute><ChurchLeaderboardPage /></ChurchFamilyRoute>} />
      
      {/* Legal Pages */}
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      
      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminAuthPage />} />
      <Route path="/admin/dashboard" element={<AdminDashboard />} />
    </Routes>
  );
}

export default function App() {
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("user")); } catch { return null; }
  });

  const login = (tok, userData) => {
    localStorage.setItem("token", tok);
    localStorage.setItem("user", JSON.stringify(userData));
    axios.defaults.headers.common["Authorization"] = `Bearer ${tok}`;
    setToken(tok);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
  };

  const updateUser = (newUserData) => {
    localStorage.setItem("user", JSON.stringify(newUserData));
    setUser(newUserData);
  };

  useEffect(() => {
    if (token) axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }, [token]);

  return (
    <LanguageProvider>
      <AuthContext.Provider value={{ token, user, login, logout, updateUser }}>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthContext.Provider>
    </LanguageProvider>
  );
}
